import {
  Control,
  Path,
  FieldValues,
  UseFormClearErrors,
} from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import NativeSelect from "@/components/ui/input/native-select";
import FormTextInput from "@/components/ui/input/form-text-input";
import FormPhoneInput from "@/components/ui/input/form-phone-input";
import FormInputDropdown from "@/components/ui/input/form-input-dropdown";
import { State } from "@/lib/country-config";
import { AddressField } from "@/networking/addresses.api";
import { AddressSuggestion } from "@/components/checkout/hooks/useAutocomplete";
import { cn } from "@/lib/utils";

interface DynamicAddressFieldsProps<TFieldValues extends FieldValues> {
  addressFields: AddressField[];
  stateOptions: State[];
  fieldPathPrefix: string;
  control: Control<TFieldValues>;
  formCountry: string;
  countryCode?: string;
  renderAutocompleteOptions: (isFocused: boolean) => React.ReactNode;
  getAutocompleteOptions: (input: string, formCountry: string) => void;
  handleOptionClick: (option: AddressSuggestion, formCountry: string) => void;
  collectName?: boolean;
  clearErrors?: UseFormClearErrors<TFieldValues>;
}

interface FieldPosition {
  isFirstInLastRow: boolean;
  isLastInLastRow: boolean;
  isOnlyFieldInLastRow: boolean;
}

export default function DynamicAddressFields<TFieldValues extends FieldValues>({
  addressFields,
  stateOptions,
  fieldPathPrefix,
  control,
  formCountry,
  countryCode,
  renderAutocompleteOptions,
  getAutocompleteOptions,
  handleOptionClick,
  collectName = true,
  clearErrors,
}: DynamicAddressFieldsProps<TFieldValues>) {
  const { t } = useTranslation();

  const getBottomRoundedStyles = (position: FieldPosition) => {
    if (position.isOnlyFieldInLastRow) {
      // Single field: rounded bottom on all screen sizes
      return "rounded-b";
    }
    if (position.isFirstInLastRow) {
      // First field: no rounding on mobile (not at bottom), rounded-bl on desktop
      return "sm:rounded-bl";
    }
    if (position.isLastInLastRow) {
      // Last field: full rounded-b on mobile (at bottom), only rounded-br on desktop
      return "rounded-b sm:rounded-bl-none";
    }
    return "";
  };

  const renderField = (field: AddressField, position: FieldPosition) => {
    const fieldPath = (
      fieldPathPrefix ? `${fieldPathPrefix}.${field.field}` : field.field
    ) as Path<TFieldValues>;
    const bottomStyles = getBottomRoundedStyles(position);

    if (!collectName && field.field === "name") {
      return null;
    }

    switch (field.field) {
      case "state":
        if (stateOptions.length > 0) {
          return (
            <NativeSelect
              key={field.field}
              control={control}
              label={field.label}
              placeholder={t("select_state")}
              options={stateOptions.map((state) => ({
                id: state.isoCode,
                name: state.name,
              }))}
              name={fieldPath}
              inputStyles={bottomStyles}
              showErrorMsg={true}
              handleInputChange={() => clearErrors?.(fieldPath)}
            />
          );
        }
        return (
          <FormTextInput
            key={field.field}
            control={control}
            name={fieldPath}
            label={field.label}
            inputStyles={cn("w-full", bottomStyles)}
            containerStyles="flex-1"
            showErrorMsg={true}
            handleInputChange={() => clearErrors?.(fieldPath)}
          />
        );

      case "phone":
        return (
          <FormPhoneInput
            key={field.field}
            control={control}
            name={fieldPath}
            label={field.label}
            country={countryCode}
            required={field.required}
            showErrorMsg={true}
            inputStyles={cn("w-full", bottomStyles)}
            handleInputChange={() => clearErrors?.(fieldPath)}
          />
        );

      case "address1":
        return (
          <FormInputDropdown
            key={field.field}
            control={control}
            name={fieldPath}
            label={field.label}
            containerStyles="flex-1"
            inputStyles={cn("w-full", bottomStyles)}
            renderAutocompleteOptions={renderAutocompleteOptions}
            getAutocompleteOptions={getAutocompleteOptions}
            handleOptionClick={handleOptionClick}
            formCountry={formCountry}
            showErrorMsg={true}
            handleInputChange={() => clearErrors?.(fieldPath)}
          />
        );

      default:
        return (
          <FormTextInput
            key={field.field}
            control={control}
            name={fieldPath}
            label={field.label}
            inputStyles={cn("w-full", bottomStyles)}
            containerStyles="flex-1"
            showErrorMsg={true}
            handleInputChange={() => clearErrors?.(fieldPath)}
            autoComplete={field.field === "name" ? "name" : undefined}
          />
        );
    }
  };

  const nameFields = addressFields.filter((f) => f.field === "name");
  const addressLineFields = addressFields.filter(
    (f) => f.field === "address1" || f.field === "address2",
  );
  const locationFields = addressFields.filter(
    (f) =>
      f.field === "city" || f.field === "state" || f.field === "postal_code",
  );
  const phoneFields = addressFields.filter((f) => f.field === "phone");

  // Determine which row is the last row with fields
  const hasPhoneFields = phoneFields.length > 0;
  const hasLocationFields = locationFields.length > 0;
  const hasAddressLineFields = addressLineFields.length > 0;
  const hasNameFields = collectName && nameFields.length > 0;

  const lastRowType = hasPhoneFields
    ? "phone"
    : hasLocationFields
      ? "location"
      : hasAddressLineFields
        ? "addressLine"
        : hasNameFields
          ? "name"
          : null;

  const getPosition = (
    rowType: "name" | "addressLine" | "location" | "phone",
    index: number,
    totalInRow: number,
  ): FieldPosition => {
    const isInLastRow = rowType === lastRowType;
    return {
      isFirstInLastRow: isInLastRow && index === 0,
      isLastInLastRow: isInLastRow && index === totalInRow - 1,
      isOnlyFieldInLastRow: isInLastRow && totalInRow === 1,
    };
  };

  return (
    <>
      {hasNameFields && (
        <div className="sm:flex">
          {nameFields.map((field, idx) =>
            renderField(field, getPosition("name", idx, nameFields.length)),
          )}
        </div>
      )}
      <div>
        {addressLineFields.map((field, idx) =>
          renderField(
            field,
            getPosition("addressLine", idx, addressLineFields.length),
          ),
        )}
      </div>
      {hasLocationFields && (
        <div className="sm:flex">
          {locationFields.map((field, idx) =>
            renderField(
              field,
              getPosition("location", idx, locationFields.length),
            ),
          )}
        </div>
      )}
      {hasPhoneFields && (
        <div>
          {phoneFields.map((field, idx) =>
            renderField(field, getPosition("phone", idx, phoneFields.length)),
          )}
        </div>
      )}
    </>
  );
}
